import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { type ListingType } from '@/features/listing/types/listing-types';
import { type GeoPage } from '@/features/place/types/geo-page-types';
import urlToPlaceName from '@/features/place/utils/url-to-place-name';
import { Link } from '@/i18n/routing';
import styles from './listings-seo-links.module.scss';
interface ListingsSeoLinksProps {
  geoPage: GeoPage;
  listingType: ListingType;
  location: string;
}
function formatPlaceLabel(token: string): string {
  return urlToPlaceName(token).split(' ').map(word => word ? `${word[0].toUpperCase()}${word.slice(1)}` : word).join(' ');
}
function formatShortLocationLabel(location: string): string {
  const parts = location.split(',').map(part => part.trim()).filter(Boolean);
  const withoutCountry = parts.length > 1 ? parts.slice(0, -1) : parts;
  const shortened = withoutCountry.length > 2 ? withoutCountry.slice(0, -1) : withoutCountry;
  const deduped = shortened.filter((part, index) => index === 0 || part.toLowerCase() !== shortened[index - 1].toLowerCase());
  return deduped.slice(0, 2).map(part => part.split(' ').map(word => word ? `${word[0].toUpperCase()}${word.slice(1).toLowerCase()}` : word).join(' ')).join(', ');
}
function getPlaceLabel(link: {
  displayName?: string;
  locationToken: string;
}): string {
  return link.displayName ?? formatPlaceLabel(link.locationToken);
}
export default function ListingsSeoLinks(props) {
  const $ = _c(17);
  const {
    geoPage,
    listingType,
    location
  } = props;
  const translationsSeo = useTranslations("seo.links");
  let title;
  if (translationsSeo.has(`title.locations.${location}.${listingType}`)) {
    const t0 = `title.locations.${location}.${listingType}`;
    let t1;
    if ($[0] !== t0 || $[1] !== translationsSeo) {
      t1 = translationsSeo(t0);
      $[0] = t0;
      $[1] = translationsSeo;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    title = t1;
  } else {
    const t0 = `title.default.${listingType}`;
    let t1;
    if ($[3] !== geoPage.locationToken || $[4] !== geoPage.searchParams.location || $[5] !== t0 || $[6] !== translationsSeo) {
      t1 = translationsSeo(t0, {
        placeName: geoPage.searchParams.location ? formatShortLocationLabel(geoPage.searchParams.location) : formatPlaceLabel(geoPage.locationToken)
      });
      $[3] = geoPage.locationToken;
      $[4] = geoPage.searchParams.location;
      $[5] = t0;
      $[6] = translationsSeo;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    title = t1;
  }
  const hasCityLinks = Boolean(geoPage.cityLinks?.length);
  const hasNeighborhoodLinks = Boolean(geoPage.neighborhoodLinks?.length);
  const neighborhoodContextToken = geoPage.cityToken ?? geoPage.areaToken ?? geoPage.locationToken;
  let t0;
  if ($[8] !== geoPage.areaToken || $[9] !== geoPage.cityLinks || $[10] !== geoPage.neighborhoodLinks || $[11] !== hasCityLinks || $[12] !== hasNeighborhoodLinks || $[13] !== neighborhoodContextToken || $[14] !== title || $[15] !== translationsSeo) {
    t0 = (hasCityLinks || hasNeighborhoodLinks) && <div className={styles.listingsSeoLinks}><h2 className={styles.listingsSeoLinks_title}>{title}</h2><div className={styles.listingsSeoLinks_wrapper}>{hasCityLinks && <div className={styles.listingsSeoLinks_container}><h4 className={styles.listingsSeoLinks_areaTitle}>{`${translationsSeo("areas-in")} ${formatPlaceLabel(geoPage.areaToken)}:`}</h4><ul className={styles.listingsSeoLinks_list}>{geoPage.cityLinks.map(_temp)}</ul></div>}{hasNeighborhoodLinks && <div className={styles.listingsSeoLinks_container}><h4 className={styles.listingsSeoLinks_areaTitle}>{`${translationsSeo("neighborhood-in")} ${formatPlaceLabel(neighborhoodContextToken)}:`}</h4><ul className={styles.listingsSeoLinks_list}>{geoPage.neighborhoodLinks.map(_temp2)}</ul></div>}</div></div>;
    $[8] = geoPage.areaToken;
    $[9] = geoPage.cityLinks;
    $[10] = geoPage.neighborhoodLinks;
    $[11] = hasCityLinks;
    $[12] = hasNeighborhoodLinks;
    $[13] = neighborhoodContextToken;
    $[14] = title;
    $[15] = translationsSeo;
    $[16] = t0;
  } else {
    t0 = $[16];
  }
  return t0;
}
function _temp2(t0) {
  const {
    displayName: displayName_0,
    locationToken: locationToken_0,
    path: path_0
  } = t0;
  const decodedPlaceName_0 = getPlaceLabel({
    displayName: displayName_0,
    locationToken: locationToken_0
  });
  return <li key={path_0} className={styles.listingsSeoLinks_item}><Link prefetch={false} href={path_0}>{decodedPlaceName_0}</Link></li>;
}
function _temp(t0) {
  const {
    displayName,
    locationToken,
    path
  } = t0;
  const decodedPlaceName = getPlaceLabel({
    displayName,
    locationToken
  });
  return <li key={path} className={styles.listingsSeoLinks_item}><Link prefetch={false} href={path}>{decodedPlaceName}</Link></li>;
}